import type {
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { cloneElement, isValidElement, useId } from 'react';

import { cn } from '@/lib/cn';

const controlClasses =
  'h-9 w-full rounded-[0.5rem] border border-line bg-paper px-3 text-sm text-ink placeholder:text-accent ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper ' +
  'disabled:opacity-50';

/** Label + control + hint/error — wraps one `Input`/`Select`/`Textarea`.
 * Vervangt `.form label`. */
export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  htmlFor?: string;
  /** The one form control this field wraps (`Input`/`Select`/`Textarea`).
   * Its `id` is set to match the label unless it already has one. */
  children: ReactElement<{ id?: string }>;
  className?: string;
}) {
  const generatedId = useId();
  const childId = isValidElement(children) ? children.props.id : undefined;
  const id = htmlFor ?? childId ?? generatedId;
  const control = isValidElement(children) ? cloneElement(children, { id }) : children;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-ink text-sm font-medium">
        {label}
      </label>
      {control}
      {hint && !error && <p className="text-accent text-[13px]">{hint}</p>}
      {error && (
        <p className="text-danger text-[13px]" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(controlClasses, className)} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(controlClasses, 'h-auto min-h-24 py-2', className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(controlClasses, className)} {...rest}>
      {children}
    </select>
  );
}

export function Checkbox({
  label,
  className,
  id,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  const generatedId = useId();
  const checkboxId = id ?? generatedId;

  return (
    <label
      htmlFor={checkboxId}
      className={cn('text-ink flex items-center gap-2 text-sm', className)}
    >
      <input
        id={checkboxId}
        type="checkbox"
        className="border-line text-accent focus-visible:ring-accent focus-visible:ring-offset-paper size-4 rounded focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        {...rest}
      />
      {label}
    </label>
  );
}

/** Form's closing row — primary right, "Cancel" as ghost before it, per
 * *Basis layout*. */
export function FormActions({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cn('flex items-center justify-end gap-3', className)}>{children}</div>;
}
